// A page shown inside one of Home's slide-up cards (src/home/Sheet.tsx) has ?card=1 in its address.
// There, leaving the page closes the card: "Home" and Escape return to Home underneath, and any
// other link goes through Home (a conversation becomes the card, any other page opens in Home's
// place), so the card never turns into a second app inside the first.

/** True inside a Home card. */
export function inCard(): boolean {
  try {
    return window.parent !== window && new URLSearchParams(window.location.search).has('card');
  } catch {
    return false;
  }
}

/** Asks Home to open its own full-screen menu over the card (one menu everywhere, founder 2026-10-18). */
export function openHomeMenu() {
  window.parent.postMessage('pl:menu', window.location.origin);
}

/** Asks Home to close the card. */
export function closeCard() {
  window.parent.postMessage('pl:close', window.location.origin);
}

/** Goes to another page: inside a card through Home (sign-in and other sites refuse to load
 * inside a card), elsewhere directly. */
export function goPage(href: string) {
  if (inCard()) leaveCard(href);
  else window.location.assign(href);
}

/** Asks Home to go to another page: a conversation opens as the card, anything else in Home's place. */
export function leaveCard(href: string) {
  window.parent.postMessage({ pl: 'go', href }, window.location.origin);
}

/** Inside a card: links to Home and Escape close it; other links open in Home's place. */
export function keepLinksOutOfCard() {
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!a || a.target || a.hasAttribute('download')) return;
    const url = new URL(a.href, window.location.href);
    if (!/^https?:$/.test(url.protocol)) return;
    // A link within the same page (#section) stays in the card.
    if (url.origin === window.location.origin && url.pathname === window.location.pathname && url.search === window.location.search) return;
    e.preventDefault();
    if (url.origin === window.location.origin && url.pathname.replace(/\/+$/, '') === '/home') closeCard();
    else leaveCard(url.href);
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeCard(); });
}

/** True when this card is the chat with her behind it (Home shows her at the desk with her notepad). */
export function withHerBehind(): boolean {
  try { return inCard() && new URLSearchParams(window.location.search).get('her') === '1'; } catch { return false; }
}

/** Tells Home that the patient has just sent a message, so she can note it down while the reply is written. */
export function toldHer() {
  if (withHerBehind()) window.parent.postMessage('pl:sent', window.location.origin);
}
